import {Component, inject, OnInit, signal, input, output} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  AiProvenanceView,
  GeneratedProjectDescriptionControllerService,
  GeneratedProjectDescriptionStatus,
  ProjectDescriptionResponse,
  TenderInvitationPreviewControllerService,
  TenderInvitationPreviewProjectDescriptionUpsertRequest,
  TenderInvitationPreviewResponse,
  TenderPhaseType,
} from '@frontend/shared-api';
import {RichTextEditorComponent} from '../../../../../../../shared/components/rich-text-editor/rich-text-editor.component';
import {LoadingComponent} from '../../../../../../../shared/components/loading/loading.component';
import {LoadingSize} from '../../../../../../../shared/components/loading/loading.types';
import {AiGeneratedLabelComponent} from '../../../../../../../shared/components/ai-generated-label/ai-generated-label.component';
import {
  ProjectStepRichTextService,
  RichTextSection,
  RichTextSectionConfig,
} from '../../../../../../../shared/services/project-step-rich-text.service';
import {Observable, of, timer} from 'rxjs';
import {
  catchError,
  concatMap,
  defaultIfEmpty,
  filter,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {ProjectBaseStepComponent} from '../../../../project-base-step/project-base-step.component';
import {TabStatus} from '../../../../../../../shared/tabs/tab-status.model';

@Component({
  selector: 'app-step-7-2-rfi-project-description',
  providers: [ProjectStepRichTextService],
  imports: [TranslocoPipe, RichTextEditorComponent, LoadingComponent, AiGeneratedLabelComponent],
  templateUrl: './step-5-2-rfi-project-description.component.html',
})
export class Step52RfiProjectDescriptionComponent
  extends ProjectBaseStepComponent
  implements OnInit
{
  private readonly invitationPreviewControllerService = inject(
    TenderInvitationPreviewControllerService,
  );
  private readonly generatedDescriptionService = inject(
    GeneratedProjectDescriptionControllerService,
  );
  readonly richTextService = inject(
    ProjectStepRichTextService<
      TenderInvitationPreviewProjectDescriptionUpsertRequest,
      TenderInvitationPreviewResponse
    >,
  );

  protected readonly formattedContent = signal<string>('');
  protected readonly descriptionProvenance = signal<AiProvenanceView | undefined>(undefined);
  protected readonly descriptionLoading = signal<boolean>(false);
  protected readonly LoadingSize = LoadingSize;

  readonly preloadedData = input<TenderInvitationPreviewResponse>();
  readonly stepLocked = input(false);
  readonly statusChange = output<TabStatus>();

  constructor() {
    super();
  }

  onStatusChange = () => {
    this.emitStatusChange();
  };

  get invitationSections() {
    return this.richTextService.getSections();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeRichTextService();
    this.loadGeneratedDescription();
  }

  private loadGeneratedDescription(): void {
    this.descriptionLoading.set(true);
    this.fetchOrGenerateDescription().subscribe({
      next: (content: string) => {
        this.formattedContent.set(content);
        this.descriptionLoading.set(false);
      },
      error: () => {
        this.formattedContent.set('');
        this.descriptionLoading.set(false);
      },
    });
  }

  private fetchOrGenerateDescription(): Observable<string> {
    return this.generatedDescriptionService.getProjectDescription({projectId: this.projectId}).pipe(
      tap((response: ProjectDescriptionResponse) => this.captureProvenance(response)),
      switchMap((response: ProjectDescriptionResponse) =>
        response.status === GeneratedProjectDescriptionStatus.READY
          ? of(response.content ?? '')
          : this.generateAndPollDescription(),
      ),
      catchError(() => of('')),
    );
  }

  private captureProvenance(response: ProjectDescriptionResponse): void {
    if (response.status === GeneratedProjectDescriptionStatus.READY) {
      this.descriptionProvenance.set(response.aiProvenance);
    }
  }

  private generateAndPollDescription(): Observable<string> {
    return this.generatedDescriptionService
      .generateProjectDescription({projectId: this.projectId})
      .pipe(
        switchMap(() => this.pollForDescription()),
        catchError(() => of('')),
      );
  }

  private pollForDescription(): Observable<string> {
    return timer(DESCRIPTION_POLL_INTERVAL_MS, DESCRIPTION_POLL_INTERVAL_MS).pipe(
      take(DESCRIPTION_POLL_MAX_ATTEMPTS),
      concatMap(() =>
        this.generatedDescriptionService.getProjectDescription({projectId: this.projectId}).pipe(
          tap((response: ProjectDescriptionResponse) => this.captureProvenance(response)),
          map((response: ProjectDescriptionResponse) =>
            response.status === GeneratedProjectDescriptionStatus.READY
              ? (response.content ?? '')
              : '',
          ),
          catchError(() => of('')),
        ),
      ),
      filter((content: string) => content.length > 0),
      take(1),
      defaultIfEmpty(''),
    );
  }

  private initializeRichTextService(): void {
    const config: RichTextSectionConfig<
      TenderInvitationPreviewProjectDescriptionUpsertRequest,
      TenderInvitationPreviewResponse
    > = {
      sections: PROJECT_DESCRIPTION_SECTIONS,
      fieldMap: PROJECT_DESCRIPTION_FIELD_MAP,
      saveFunction: (projectId, payload) => this.saveWithCompleteDto(projectId, payload),
      loadFunction: (projectId) =>
        this.invitationPreviewControllerService.getTenderInvitationPreview({
          projectId,
          phase: TenderPhaseType.RFI,
        }),
    };

    this.richTextService.initialize(this.projectId, config, this.preloadedData());
  }

  private saveWithCompleteDto(
    projectId: number,
    richTextPayload: TenderInvitationPreviewProjectDescriptionUpsertRequest,
  ): Observable<TenderInvitationPreviewResponse> {
    return this.invitationPreviewControllerService
      .getTenderInvitationPreview({
        projectId,
        phase: TenderPhaseType.RFI,
      })
      .pipe(
        switchMap((existingData) => {
          const completePayload: TenderInvitationPreviewProjectDescriptionUpsertRequest = {
            ...existingData,
            ...richTextPayload,
          };
          return this.invitationPreviewControllerService.upsertProjectDescription({
            projectId,
            phase: TenderPhaseType.RFI,
            tenderInvitationPreviewProjectDescriptionUpsertRequest: completePayload,
          });
        }),
      );
  }

  protected emitStatusChange(): void {
    const totalSections = this.richTextService.getTotalSectionsCount();
    const completedSections = this.richTextService.getCompletedSectionsCount();

    this.statusChange.emit({totalSections, completedSections});
  }
}

const DESCRIPTION_POLL_INTERVAL_MS = 3000;
const DESCRIPTION_POLL_MAX_ATTEMPTS = 60;

export const PROJECT_DESCRIPTION_SECTIONS: RichTextSection[] = [
  {
    key: 'step_rfi_invitation_preview_beginning_text',
    content: '',
    tooltipKey: 'project.steps.invitationTemplate.beginningText',
    hasChanges: false,
  },
  {
    key: 'step_rfi_invitation_preview_end_text',
    content: '',
    tooltipKey: 'project.steps.invitationTemplate.endText',
    hasChanges: false,
  },
];

export const PROJECT_DESCRIPTION_FIELD_MAP: Record<
  string,
  keyof TenderInvitationPreviewProjectDescriptionUpsertRequest
> = {
  step_rfi_invitation_preview_beginning_text: 'beginningText',
  step_rfi_invitation_preview_end_text: 'endText',
};
